import { useCallback, useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { exigirConfiguracionEstudio } from '@/services/configuracionService'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useSupabase } from '@/lib/supabase-context'
import { recibosService } from '@/services/recibosService'
import { emailService } from '@/services/emailService'
import type { DatosReciboPDF } from '@/lib/pdfReciboPago'
import { urlToDataUrl } from '@/lib/imagenDataUrl'
import type { ReciboParaFactura } from '@/services/facturasService'

/** Lo que devuelve `enviar`: distingue "salió" de "no se pudo, y por qué". */
export type ResultadoEnvioRecibo =
  | { estado: 'enviado' }
  | { estado: 'sin-email'; clienteNombre: string }
  | { estado: 'error'; error: unknown }

/** Lo que devuelve `prepararParaFactura`: el recibo listo para el pedido de factura, o por qué no está. */
export type ResultadoReciboParaFactura =
  | { estado: 'listo'; recibo: ReciboParaFactura }
  | { estado: 'sin-email'; clienteNombre: string }
  | { estado: 'error'; error: unknown }

const aBase64 = async (blob: Blob) => {
  const buffer = await blob.arrayBuffer()
  return btoa(new Uint8Array(buffer).reduce((acc, byte) => acc + String.fromCharCode(byte), ''))
}

/**
 * Corre el envío y traduce cualquier falla a un resultado. **Nunca lanza.**
 *
 * Es la garantía central de la feature: se llama cuando el pago YA está commiteado en la base, así
 * que una excepción que suba desde acá rompería el registro de un cobro por un problema de mail.
 * Vive afuera del hook para poder probarse sin montar React.
 */
export const ejecutarEnvioRecibo = async (
  accion: () => Promise<ResultadoEnvioRecibo>,
): Promise<ResultadoEnvioRecibo> => {
  try {
    return await accion()
  } catch (error) {
    console.error('Error enviando el recibo de pago:', error)
    return { estado: 'error', error }
  }
}

/**
 * Traduce la respuesta del servicio de mail a un resultado.
 *
 * `emailService` NO lanza cuando el servidor rechaza el envío: devuelve `{success:false}`. Ignorar
 * ese campo hacía que la UI dijera "Recibo enviado al cliente" con el mail sin salir — destinatario
 * suprimido, rate limit por hora o Resend caído se veían como éxito (ORR-354). Vive afuera del hook,
 * como `ejecutarEnvioRecibo`, para poder probarse sin montar React.
 */
export const interpretarEnvio = (
  envio: { success: boolean; error?: string },
): ResultadoEnvioRecibo =>
  envio.success
    ? { estado: 'enviado' }
    : { estado: 'error', error: envio.error ?? 'No se pudo enviar el recibo' }

/**
 * Carga los módulos que arman el PDF.
 *
 * Vive afuera de `generar` porque el momento de la PRIMERA carga decide qué se rompe cuando el
 * chunk quedó viejo: Netlify responde el `index.html` para un hash que ya no publica, el
 * `import()` falla, Vite emite `vite:preloadError` y `preloadErrorRecovery` recarga la página.
 * Disparado desde `generar` eso cae con el pago ya commiteado, y se lleva el recibo y el aviso
 * (ORR-394). Precargarlo lo adelanta a un momento sin nada que perder.
 */
export const cargarModulosRecibo = () =>
  Promise.all([import('@react-pdf/renderer'), import('@/lib/pdfReciboPago')])

/**
 * Genera, descarga y envía el recibo de pago de un cobro registrado (ORR-336).
 *
 * Los datos salen de la base a partir del id del pago, no del formulario, así que el recibo de un
 * cobro de hace meses es idéntico al que se emitió en su momento — y el que se descarga es el mismo
 * que se mandó por mail.
 */
export function useReciboPago({ precargar = false }: { precargar?: boolean } = {}) {
  const { data: config, isError: configError } = useConfiguracionEstudio()
  const { organizacion } = useOrganizacion()
  const { supabase } = useSupabase()
  const { getAccessTokenSilently } = useAuth0()
  const [generando, setGenerando] = useState(false)

  // Quien abre un cobro va a emitir un recibo: bajar el chunk del PDF con el formulario todavía
  // vacío cuesta lo mismo y cambia el costo del fallo. El rechazo se traga acá a propósito — lo
  // que hay que hacer con él ya lo hizo `preloadErrorRecovery` al recibir el evento de Vite.
  useEffect(() => {
    if (!precargar) return
    void cargarModulosRecibo().catch(() => {})
  }, [precargar])

  /** Arma el encabezado y la firma del recibo. Firma y sello van como data URL, como en el contrato. */
  const armarEstudio = useCallback(async (): Promise<DatosReciboPDF['estudio']> => {
    const estudio = exigirConfiguracionEstudio({ data: config, isError: configError })
    const [firmaUrl, selloUrl] = await Promise.all([
      urlToDataUrl(estudio?.firma_email_imagen_url),
      urlToDataUrl(estudio?.firma_email_sello_url),
    ])
    return {
      nombre: estudio?.nombre ?? 'Estudio Contable',
      cuit: estudio?.cuit ?? '',
      direccion: estudio?.direccion ?? '',
      telefono: estudio?.telefono ?? '',
      email: estudio?.email ?? '',
      logoUrl:
        organizacion?.branding?.logo_light_url ||
        organizacion?.branding?.logo_dark_url ||
        undefined,
      firmaUrl,
      selloUrl,
      responsable: estudio?.responsable_nombre?.trim() || undefined,
      matricula: estudio?.matricula_profesional?.trim() || undefined,
    }
  }, [config, configError, organizacion])

  /** Lee los datos y renderiza el PDF. Los módulos van lazy: la lib pesa y solo hace falta acá. */
  const generar = useCallback(
    async (pagoId: string) => {
      if (!supabase || !organizacion?.id) {
        throw new Error('No hay sesión activa para generar el recibo')
      }

      const [recibo, [{ pdf }, { ReciboPagoPDF }], estudio] = await Promise.all([
        recibosService.obtenerDatosRecibo(supabase, pagoId, organizacion.id),
        cargarModulosRecibo(),
        armarEstudio(),
      ])

      const blob = await pdf(
        <ReciboPagoPDF datos={{ ...recibo.datos, estudio }} />,
      ).toBlob()

      return { recibo, blob }
    },
    [supabase, organizacion, armarEstudio],
  )

  const descargar = useCallback(
    async (pagoId: string, filename?: string) => {
      setGenerando(true)
      try {
        const { recibo, blob } = await generar(pagoId)

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename ?? `recibo-${recibo.datos.pago.fecha}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } finally {
        setGenerando(false)
      }
    },
    [generar],
  )

  /**
   * Manda el recibo al cliente con el PDF adjunto.
   *
   * **Nunca lanza**: se llama después de que el pago ya está commiteado en la base, así que un
   * fallo de mail no puede romper ni revertir el cobro. Devuelve qué pasó para que quien llama
   * decida cómo avisarlo.
   */
  const enviar = useCallback(
    async (pagoId: string): Promise<ResultadoEnvioRecibo> => {
      setGenerando(true)
      return ejecutarEnvioRecibo(async () => {
        const organizacionId = organizacion?.id
        if (!organizacionId) throw new Error('No hay sesión activa para enviar el recibo')

        const { recibo, blob } = await generar(pagoId)

        if (!recibo.emailCliente) {
          return { estado: 'sin-email', clienteNombre: recibo.datos.cliente.nombre }
        }

        const buffer = await blob.arrayBuffer()
        const base64 = btoa(
          new Uint8Array(buffer).reduce((acc, byte) => acc + String.fromCharCode(byte), ''),
        )

        const token = await getAccessTokenSilently()
        const envio = await emailService.enviarReciboPago(
          token,
          recibo.emailCliente,
          {
            clienteNombre: recibo.datos.cliente.nombre,
            monto: recibo.datos.pago.monto,
            fecha: recibo.datos.pago.fecha,
            lineas: recibo.datos.lineas,
            reciboBase64: base64,
            whatsappEstudio: config?.whatsapp,
            nombreEstudio: config?.nombre,
          },
          organizacionId,
          recibo.clienteId,
        )

        return interpretarEnvio(envio)
      }).finally(() => setGenerando(false))
    },
    [generar, getAccessTokenSilently, config, organizacion],
  )

  /**
   * Arma el recibo de un cobro para mandarlo con el pedido de factura, sin enviarlo. **Nunca lanza**, por
   * el mismo motivo que `enviar`.
   */
  const prepararParaFactura = useCallback(
    async (pagoId: string): Promise<ResultadoReciboParaFactura> => {
      setGenerando(true)
      try {
        const { recibo, blob } = await generar(pagoId)
        if (!recibo.emailCliente) return { estado: 'sin-email', clienteNombre: recibo.datos.cliente.nombre }
        return {
          estado: 'listo',
          recibo: {
            pdf: await aBase64(blob),
            clienteNombre: recibo.datos.cliente.nombre,
            monto: recibo.datos.pago.monto,
            fecha: recibo.datos.pago.fecha.slice(0, 10),
            lineas: recibo.datos.lineas,
          },
        }
      } catch (error) {
        console.error('Error armando el recibo para la factura:', error)
        return { estado: 'error', error }
      } finally {
        setGenerando(false)
      }
    },
    [generar],
  )

  return { descargar, enviar, prepararParaFactura, generando }
}
